import BackendConfig from "src/backendConfig";
import { CreateProject } from "./project";

const basepath = BackendConfig.httpBackend;

function cleanHeaders(headers) {
  const cleanedHeaders = { ...headers };
  Object.keys(cleanedHeaders).forEach((key) => {
    if (cleanedHeaders[key] === undefined || cleanedHeaders[key] === null) {
      delete cleanedHeaders[key];
    }
  });
  return cleanedHeaders;
}

async function apiRequest(method, endpoint, data = {}, options = {}) {
  const {
    apiPrefix = "",
    baseUrl = basepath,
    headers = {},
    credentials,
  } = options;

  const requestHeaders = cleanHeaders({
    "Accept": "application/json",
    "Content-Type": "application/json",
    ...headers,
  });

  const reqOptions = {
    method,
    headers: requestHeaders,
    credentials: "include"
  };

  if (credentials !== undefined) {
    reqOptions.credentials = credentials;
  }

  if (method !== "GET") {
    reqOptions.body = JSON.stringify(data);
  }

  const res = await fetch(`${baseUrl}${apiPrefix}${endpoint}`, reqOptions);
  return res;
}

async function getWsApi(endpoint, options = {}) {
  const { apiPrefix = "/nex-api", ...requestOptions } = options;
  return apiRequest("GET", endpoint, undefined, { apiPrefix, ...requestOptions });
}

async function postWsApi(endpoint, data = {}, options = {}) {
  const { apiPrefix = "/nex-api", ...requestOptions } = options;
  return apiRequest("POST", endpoint, data, { apiPrefix, ...requestOptions });
}

async function getApi(endpoint, options = {}) {
  const { apiPrefix = "/nex-api-public", ...requestOptions } = options;
  return apiRequest("GET", endpoint, undefined, { apiPrefix, ...requestOptions });
}

async function postApi(endpoint, data = {}, options = {}) {
  const { apiPrefix = "/nex-api-public", ...requestOptions } = options;
  return apiRequest("POST", endpoint, data, { apiPrefix, ...requestOptions });
}

async function deleteApi(endpoint, data = {}, options = {}) {
  const { apiPrefix = "/nex-api-public", ...requestOptions } = options;
  return apiRequest("DELETE", endpoint, data, { apiPrefix, ...requestOptions });
}

async function getAdminApi(endpoint, options = {}) {
  const { apiPrefix = "/nex-api-admin", headers = {}, ...requestOptions } = options;
  const token = window.sessionStorage.getItem("AgentAPI.Token");
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  return apiRequest("GET", endpoint, undefined, {
    apiPrefix,
    headers: {
      ...authHeaders,
      ...headers,
    },
    ...requestOptions,
  });
}

async function postAdminApi(endpoint, data = {}, options = {}) {
  const { apiPrefix = "/nex-api-admin", headers = {}, ...requestOptions } = options;
  const token = window.sessionStorage.getItem("AgentAPI.Token");
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  return apiRequest("POST", endpoint, data, {
    apiPrefix,
    headers: {
      ...authHeaders,
      ...headers,
    },
    ...requestOptions,
  });
}

async function deleteAdminApi(endpoint, data = {}, options = {}) {
  const { apiPrefix = "/nex-api-admin", headers = {}, ...requestOptions } = options;
  const token = window.sessionStorage.getItem("AgentAPI.Token");
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  return deleteApi(endpoint, data, {
    apiPrefix,
    headers: {
      ...authHeaders,
      ...headers,
    },
    ...requestOptions,
  });
}

async function parseSafeJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

async function ensureAdminSuccess(res, fallbackMessage) {
  const payload = await parseSafeJson(res);

  if (!res.ok) {
    const errorCode = payload?.error_code || payload?.errorCode || payload?.code;
    const message = payload?.message || payload?.error || errorCode || fallbackMessage;
    const err = new Error(message || fallbackMessage);
    err.status = res.status;
    err.code = errorCode;
    err.payload = payload;
    throw err;
  }

  return payload;
}

export async function getProjectStatistics() {

}

export async function getQuickLoginServiceId() {
  const res = await getWsApi("/quickLoginServiceId.ws", {
    credentials: "include"
  });

  const json = await res.json();
  return json.serviceId;
}


export async function debugQuickLogin() {
  const res = await getWsApi("/debugQuickLogin.ws", {
    headers: {
      "Accept": "text/plain",
      "Content-Type": undefined
    },
  });
  const text = await res.text();
  console.log(text);
}
export async function getPurchaseStatus(contractId) {
  const res = await postWsApi("/purchaseStatus.ws", { contractId });

  const data = await res.json();

  if (!data.ok) {
    throw new Error(data.error || "Unknown error from purchaseStatus");
  }

  return data; // { ok, status, contractId, contractState, ownerSigned, tokenId?, token? }
}
export async function getPortfolioTokens() {
  const res = await getWsApi("/getBoughtTokens.ws", {
    credentials: "include"
  });

  const data = await res.json();
  if (!data.ok) {
    throw new Error(data.error || "Failed to load portfolio");
  }
  return data;
}

const useLocalApiProxy = process.env.REACT_APP_USE_LOCAL_API_PROXY === "true";
const adminBase = useLocalApiProxy ? "/neuro-admin-api" : "https://api.neuro-admin.com";
const neuroPayToken = process.env.REACT_APP_NEUROPAY_TOKEN;
const issuerCreateAccountApiDomain = useLocalApiProxy
  ? "/issuer-api"
  : String(process.env.REACT_APP_ISSUER_CREATE_API_DOMAIN || "").trim().replace(/\/$/, "");
const issuerCreateAccountApiUrl = issuerCreateAccountApiDomain
  ? `${issuerCreateAccountApiDomain}/api/auth/account/create`
  : String(process.env.REACT_APP_ISSUER_CREATE_API_URL || "").trim();

function jwt() {
  const token = window.sessionStorage.getItem("AgentAPI.Token");
  return token;
}
async function request(path, body) {
  const req = await postWsApi(path, body, { apiPrefix: "" });
  return req;
}

// Create a payment checkout session via NeuroPay (neuro-admin)
export async function createCheckoutSession({ amount, currency, message, identifier, capture = true, returnUrl, returnUrlCancel, metaData, }) {
  const token = neuroPayToken;
  console.log(token);
  if (!token) {
    throw new Error("Missing NeuroPay Bearer token. Set REACT_APP_NEUROPAY_TOKEN in env.");
  }
  const body = {
    amount,
    currency,
    message,
    identifier,
    capture,
    returnUrl,
    returnUrlCancel,
    metaData,
  };
  const res = await postApi("/checkout/create", body, {
    apiPrefix: "",
    baseUrl: adminBase,
    headers: {
      "Authorization": `Bearer ${token}`,
    },
    credentials: "omit",
  });
  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(`Checkout create failed: ${res.status} ${res.statusText} ${errBody}`);
  }
  return await res.json();
}



export async function getContracts() {
  try {
    const res = await request("/contracts.ws");
    const json = await res.json();
    return json.Contracts;
  } catch (err) {
    console.log(err);
    return [];
  }
}

export async function createContract(templateId, parameters, amount, signAsCreator = false) {
  const formattedArray = Object.entries(parameters).map(([key, value]) => {
    if (value[0] === "$") {
      return {
        name: key,
        value: amount
      };
    } else {
      return {
        name: key,
        value: value
      };
    }
  });

  const objBody = { "ParametersVector": formattedArray, "templateId": templateId, "sign": signAsCreator };

  const res = await postWsApi("/createContract.ws", objBody, { apiPrefix: "" });
  const body = await res.json();

  return body;
}

export async function createCreturnerContract(amount, certificateName) {
  const objBody = { "amount": amount, "BuyerName": certificateName };
  const res = await postWsApi("/createContractCreturner.ws", objBody, { apiPrefix: "" });
  const body = await res.json();
  return body;
}

export async function petitionContract(contractId, role, message = "Purchase on Neuro-Exchange") {
  const objBody = {
    "contractId": contractId,
    "role": role,
    "message": message
  };

  const res = await postWsApi("/petitionContract.ws", objBody, { apiPrefix: "" });

  const body = await res.json();
  return body;
}

export async function getTokens() {
  try {
    // Calls the .ws file we created above
    const res = await getWsApi("/getBoughtTokens.ws", {
      // IMPORTANT: This passes the session cookies so AuthenticateSession works
      credentials: "include"
    });

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    const body = await res.json();
    console.log("Tokens received:", body);
    return body;

  } catch (error) {
    console.error("Failed to fetch tokens:", error);
    return null;
  }
}
export async function getPendingContractsForUser() {
  try {
    // Calls the .ws file we created above
    const res = await getWsApi("/getPendingContracts.ws", {
      // IMPORTANT: This passes the session cookies so AuthenticateSession works
      credentials: "include"
    });

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    const raw = await res.text();
    const trimmed = String(raw || '').trim();
    const body = trimmed ? JSON.parse(trimmed) : {};
    console.log("Pending contracts received:", body);
    return body;

  } catch (error) {
    console.error("Failed to fetch pending contracts:", error);
    return null;
  }
}
export async function getPresentReport(contractId, tokenId) {
  const objBody = {
    "contractId": contractId,
    "tokenId": tokenId,
  };

  const res = await postWsApi("/getPresentReport.ws", objBody, { apiPrefix: "" });

  if (!res.ok) {
    throw new Error("Failed to fetch the present report");
  }

  const response = await res.json();
  let base64Report = response || "";
  base64Report = base64Report.slice(13, -9);

  return base64Report;
}
export async function getAdminContracts() {
  const objBody = {
    "jwt": jwt()
  };

  const res = await postWsApi("/getAdminContracts.ws", objBody, { apiPrefix: "" });

  const body = await res.json();
  return body;
}

export function getSignedStatus(contractId, tabId, isEDalerContract = false) {
  if (contractId == undefined) {
    return;
  }
  const objBody = {
    "contractId": contractId,
    "tabId": tabId,
    "isEDalerContract": isEDalerContract
  };

  postWsApi("/getSignedStatus.ws", objBody, { apiPrefix: "" });

  return;
}

export function cancelContract(contractId) {
  if (contractId == undefined) {
    return;
  }
  const objBody = {
    "contractId": contractId
  };

  postWsApi("/cancelContract.ws", objBody, { apiPrefix: "" });

  return;
}

export async function getSalt(account, amount, country, description) {
  const objBody = {
    "account": account,
    "amount": amount,
    "country": country,
    "description": description,
    "callback": "ProcessResult",
    "state": "Purchase of Token"
  };
  const res = await postWsApi("/salt.ws", objBody);

  const body = await res.json();

  return body;
}

export async function defineCallback(tabId) {
  const objBody = {
    "tabId": tabId
  };
  const res = await postWsApi("/defineCallback.ws", objBody);
  console.log(res);
}



// only now because there is a bug with the neuron wherer it dose not seem to get all Post parameters some times.
async function getContractWithRetry(contractId, tries = 1) {
  const res = await request(`/contract.ws`, {
    id: contractId,
  });

  if (res.ok) {
    const json = await res.json();
    return json;
  }

  if (res.status === 400) {
    return undefined;
  }

  if (tries === 3) {
    throw "Somthing went wrong";
  }

  return await getContractWithRetry(contractId, tries + 1);
}

export async function getUnsignedContracts(legalId) {
  const extractUnsigned = (json) => {
    if (Array.isArray(json)) return json;
    if (Array.isArray(json?.contractIds)) return json.contractIds;
    if (Array.isArray(json?.unsigned)) return json.unsigned;
    if (Array.isArray(json?.contracts)) return json.contracts;
    if (Array.isArray(json?.data?.contractIds)) return json.data.contractIds;
    if (Array.isArray(json?.data?.unsigned)) return json.data.unsigned;
    if (Array.isArray(json?.result?.contractIds)) return json.result.contractIds;
    if (Array.isArray(json?.data)) return json.data;
    return [];
  };

  try {
    const publicRes = await getApi("/wallet/contracts/unsigned", {
      credentials: "include",
    });

    if (publicRes.ok) {
      const json = await publicRes.json();
      return extractUnsigned(json)
        .map((item) => {
          if (typeof item === "string") return item;
          if (typeof item?.contractId === "string") return item.contractId;
          if (typeof item?.id === "string") return item.id;
          return null;
        })
        .filter(Boolean);
    }
  } catch (_error) {
    // fallback to legacy endpoint below
  }

  const res = await postWsApi("/unsignedContracts.ws", {
    legalId: legalId
  });

  if (res.ok) {
    const json = await res.json();
    return extractUnsigned(json)
      .map((item) => {
        if (typeof item === "string") return item;
        if (typeof item?.contractId === "string") return item.contractId;
        if (typeof item?.id === "string") return item.id;
        return null;
      })
      .filter(Boolean);
  }

  return [];
}

export function QRSource(tagsign) {
  return basepath + "/QR/" + tagsign;
}

export async function getContract(contractId) {
  try {
    return getContractWithRetry(contractId);
  } catch (err) {
    return undefined;
  }
}

async function getProjects(localization = null) {
  let url = "/project";

  if (localization) {
    url += `?localization=${encodeURIComponent(localization)}`;
  }

  const res = await getApi(url);
  if (!res.ok) {
    throw new Error(`Failed to load projects: ${res.status}`);
  }

  const json = await res.json();
  const rows = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];

  const mapped = rows
    .map((project) => {
      try {
        return CreateProject(project, localization || "pt-BR");
      } catch (error) {
        console.warn("[NexApi.getProjects] skipping invalid project", { project, error });
        return null;
      }
    })
    .filter(Boolean);

  return mapped;
}

async function getProject(projectId, localization = null) {
  let url = `/project/${projectId}`;

  if (localization) {
    url += `?localization=${encodeURIComponent(localization)}`;
  }

  const res = await getApi(url);
  if (!res.ok) {
    throw new Error(`Failed to load projects: ${res.status}`);
  }
  const json = await res.json();
  return CreateProject(json, localization || "pt-BR");
}

async function createBridgePaymentSession(payload) {
  const method = payload.method;

  if (method === "pix") {
    const res = await postApi("/payment_session", {
      method: "pix",
      project_id: payload.projectId,
      token_count: payload.tokenCount,
      tab_id: payload.tabId
    });

    return await res.json();
  }

  const res = await postApi("/payment_session", {
    method: "crypto",
    project_id: payload.projectId,
    token_count: payload.tokenCount,
    tab_id: payload.tabId,
    amount: payload.amount,
    source: {
      wallet: payload.sourceAddress,
      payment_rail: payload.sourceRail.toLowerCase(),
      currency: payload.sourceCurrency.toLowerCase()
    }
  });

  return await res.json();
}

async function createOrder({ projectId, tokenAmount, paymentMethod, email }) {
  const res = await postApi("/order", {
    project_id: projectId,
    token_amount: tokenAmount,
    payment_method: paymentMethod,
    email,
  });

  const payload = await parseSafeJson(res);
  if (!res.ok) {
    const errorCode = payload?.errorCode || payload?.error_code || payload?.code;
    const error = new Error(payload?.message || errorCode || "Could not create the order.");
    error.status = res.status;
    error.code = errorCode;
    error.payload = payload;
    throw error;
  }

  return payload;
}

async function listOrders() {
  const res = await getApi("/order");
  const payload = await parseSafeJson(res);

  if (!res.ok) {
    const errorCode = payload?.errorCode || payload?.error_code || payload?.code;
    const error = new Error(payload?.message || errorCode || "Could not load orders.");
    error.status = res.status;
    error.code = errorCode;
    error.payload = payload;
    throw error;
  }

  return payload;
}

async function isAuthenticated() {
  const res = await getApi("/auth");
  return res.ok;
}

async function isIssuerAuthenticated() {
  const token =
    window.sessionStorage.getItem("AgentAPI.Token") ||
    window.localStorage.getItem("AgentAPI.Token") ||
    "";

  if (!token || typeof token !== "string") {
    return false;
  }

  const parts = token.split(".");
  if (parts.length !== 3) {
    return false;
  }

  try {
    const base64Payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const normalized = base64Payload.padEnd(base64Payload.length + ((4 - (base64Payload.length % 4)) % 4), "=");
    const payload = JSON.parse(window.atob(normalized));
    const exp = Number(payload?.exp);

    // If expiration exists, ensure JWT is still valid using current epoch time in seconds.
    if (Number.isFinite(exp) && exp > 0) {
      return Math.floor(Date.now() / 1000) < exp;
    }

    return true;
  } catch {
    return false;
  }
}

async function listLinkedIssuers(localization = null) {
  const query = localization ? `?localization=${encodeURIComponent(localization)}` : "";
  const res = await getAdminApi(`/user/issuers${query}`);
  const data = await ensureAdminSuccess(res, "Failed to list linked issuers.");
  return Array.isArray(data?.data) ? data.data : [];
}

async function leaveIssuer(issuerId) {
  const res = await deleteAdminApi(`/user/issuers/${encodeURIComponent(issuerId)}`);
  await ensureAdminSuccess(res, "Failed to leave issuer.");
  return true;
}

async function listInvitations() {
  const res = await getAdminApi("/user/invitations");
  const data = await ensureAdminSuccess(res, "Failed to list invitations.");
  return Array.isArray(data?.data) ? data.data : [];
}

async function acceptInvitation(invitationId) {
  const res = await postAdminApi(`/user/invitations/${encodeURIComponent(invitationId)}/accept`, {});
  await ensureAdminSuccess(res, "Failed to accept invitation.");
  return true;
}

async function declineInvitation(invitationId) {
  const res = await postAdminApi(`/user/invitations/${encodeURIComponent(invitationId)}/decline`, {});
  await ensureAdminSuccess(res, "Failed to decline invitation.");
  return true;
}

async function inviteToIssuer(issuerId, subject) {
  const res = await postAdminApi(`/issuer/${encodeURIComponent(issuerId)}/invitations`, { subject });
  const data = await ensureAdminSuccess(res, "Failed to send invitation.");
  return data;
}

async function deleteIssuerInvitation(issuerId, invitationId) {
  const res = await deleteAdminApi(`/issuer/${encodeURIComponent(issuerId)}/invitations/${encodeURIComponent(invitationId)}`);
  await ensureAdminSuccess(res, "Failed to delete invitation.");
  return true;
}

async function callIssuerAccountRoute(path, body, fallbackMessage) {
  if (!issuerCreateAccountApiUrl) {
    const err = new Error("Issuer self-registration is currently unavailable in this environment. Please contact support.");
    err.code = "registration_unavailable";
    throw err;
  }

  const routeUrl = issuerCreateAccountApiUrl.replace(/\/api\/auth\/account\/create$/, path);
  const res = await fetch(routeUrl, {
    method: "POST",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(cleanHeaders(body)),
  });

  const payload = await parseSafeJson(res);
  const responseData = payload && typeof payload === "object" && payload.data !== undefined
    ? payload.data
    : payload;

  if (!res.ok) {
    const errorCode = responseData?.error_code || responseData?.errorCode || responseData?.code || payload?.error_code || payload?.errorCode || payload?.code;
    const message = payload?.message || responseData?.message || responseData?.error || errorCode || fallbackMessage;
    const err = new Error(message);
    err.status = res.status;
    err.code = errorCode;
    err.payload = responseData || payload;
    throw err;
  }

  return responseData;
}

async function createIssuerAccount({ userName, eMail, phoneNr, password, seconds = 3600 }) {
  return callIssuerAccountRoute("/api/auth/account/create", {
    userName,
    eMail,
    phoneNr,
    password,
    seconds,
  }, "Could not create issuer account.");
}

function buildSalesQuery(params = {}) {
  const query = new URLSearchParams();
  const {
    limit,
    offset,
    continuationToken,
    extra,
    paid,
    buyerLegalId,
    createdContractId,
  } = params;

  query.set("limit", String(Number(limit) || 25));

  if (continuationToken) {
    query.set("continuation_token", String(continuationToken));
  } else {
    query.set("offset", String(Number(offset) || 0));
  }

  if (paid === true || paid === false) {
    query.set("paid", String(paid));
  }

  if (extra === true || extra === false) {
    query.set("extra", String(extra));
  }

  if (buyerLegalId) {
    query.set("buyer_legal_id", String(buyerLegalId));
  }

  if (createdContractId) {
    query.set("created_contract_id", String(createdContractId));
  }

  const serialized = query.toString();
  return serialized ? `?${serialized}` : "";
}

function mapSaleItem(item = {}) {
  return {
    id: item.id ?? null,
    project_id: item.project_id ?? null,
    paid: Boolean(item.paid),
    token_count: Number(item.token_count ?? 0),
    paying_price: Number(item.paying_price ?? 0),
    token_valuation: Number(item.token_valuation ?? 0),
    token_valuation_currency: item.token_valuation_currency ?? null,
    currency: item.currency ?? null,
    created_contract_id: item.created_contract_id ?? null,
    created: item.created ?? null,
    buyer_legal_id: item.buyer_legal_id ?? null,
    full_name: item.full_name ?? null,
    email: item.email ?? null,
    extra: {
      project_name: item?.extra?.project_name ?? null,
      issuer_name: item?.extra?.issuer_name ?? null,
    },
  };
}

function mapSalesResponse(payload = {}) {
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  return {
    data: rows.map(mapSaleItem),
    count: Number(payload?.count ?? rows.length ?? 0),
    continuation_token: payload?.continuation_token || null,
  };
}

async function listIssuerSales(issuerId, options = {}) {
  const query = buildSalesQuery(options);
  const res = await getAdminApi(`/issuer/${encodeURIComponent(issuerId)}/sales${query}`);
  const payload = await ensureAdminSuccess(res, "Failed to list issuer sales.");
  return mapSalesResponse(payload);
}

async function listProjectSales(projectId, options = {}) {
  const query = buildSalesQuery(options);
  const res = await getAdminApi(`/project/${encodeURIComponent(projectId)}/sales${query}`);
  const payload = await ensureAdminSuccess(res, "Failed to list project sales.");
  return mapSalesResponse(payload);
}

function buildNeuroPayPaymentBody(projectId, tokenCount, currency, country, returnUrl, returnUrlCancel, tabId) {
  const jwtToken = window.sessionStorage.getItem("AgentAPI.Token");

  return {
    "project_id": projectId,
    "token_count": tokenCount,
    "currency": currency,
    "country": country,
    "cmd": "InitiatePayment",
    "tab_id": tabId,
    "jwt": jwtToken,
    "return_url": returnUrl,
    "return_url_cancel": returnUrlCancel,
  };
}

async function createNeuroPayCheckout(projectId, tokenCount, currency, tabId, country, returnUrl, returnUrlCancel) {
  console.log('[checkout] defineCallback', { tabId });
  const objBody = buildNeuroPayPaymentBody(projectId, tokenCount, currency, country, returnUrl, returnUrlCancel, tabId);
  console.log('[checkout] objBody', objBody);

  const res = await postApi("/payment_session/neuropay", objBody);
  const body = await res.json();

  return {
    checkoutId: body.checkout_id,
    checkoutUrl: body.checkout_url
  };
}

async function getNeuroPayFinalPrice(projectId, tokenCount, currency, country, returnUrl, returnUrlCancel) {
  const tabId = window.sessionStorage.getItem("tabId") || "tab-" + Date.now();
  const body = buildNeuroPayPaymentBody(projectId, tokenCount, currency, country, returnUrl, returnUrlCancel, tabId);
  const res = await postApi("/payment_session/neuropay/price", body);
  const data = await parseSafeJson(res);

  if (!res.ok) {
    const message = data?.message || data?.error || "Failed to fetch NeuroPay final price.";
    const err = new Error(message);
    err.status = res.status;
    err.payload = data;
    throw err;
  }

  return {
    price: data?.price,
    currency: data?.currency,
    conversion: data?.convertion
      ? {
        rate: data.convertion.rate,
        marginPercent: data.convertion.margin_percent,
      }
      : null,
  };
}

async function completeNeuroPayCheckout(checkoutId) {
  const res = await postApi(`/payment_session/neuropay/${checkoutId}/complete`, {
    "checkout_id": checkoutId
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error("Contract signing failed. Please contact support.");
  }

  if (data.success) {
    return {
      success: data.success,
      contractId: data.contract_id
    };
  }

  return {
    success: data.success,
    message: data.message
  };
}

export async function getNeuroPayCheckout(checkoutId) {
  const res = await getApi(`/payment_session/neuropay/${checkoutId}`);

  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(`Checkout retrieve failed: ${res.status} ${res.statusText} ${errBody}`);
  }

  const data = await res.json();

  return {
    checkoutId: data.checkout_id,
    checkoutUrl: data.checkout_url,
    status: data.status
  };
}

export const NexApi = {
  isAuthenticated: isAuthenticated,
  getProjects: getProjects,
  getProject: getProject,
  issuer: {
    isAuthenticated: isIssuerAuthenticated,
    listLinkedIssuers: listLinkedIssuers,
    listIssuerSales: listIssuerSales,
    listProjectSales: listProjectSales,
    createAccount: createIssuerAccount,
    leaveIssuer: leaveIssuer,
    listInvitations: listInvitations,
    acceptInvitation: acceptInvitation,
    declineInvitation: declineInvitation,
    inviteToIssuer: inviteToIssuer,
    deleteIssuerInvitation: deleteIssuerInvitation,
  },
  bridge: {
    createPaymentSession: createBridgePaymentSession
  },
  orders: {
    create: createOrder,
    list: listOrders,
  },
  neuroPay: {
    createCheckout: createNeuroPayCheckout,
    getFinalPrice: getNeuroPayFinalPrice,
    completeCheckout: completeNeuroPayCheckout,
    getCheckout: getNeuroPayCheckout
  }
};
